import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, LogOut, Lock, Clock, RefreshCw, Eye, EyeOff, Users, ClipboardCheck, Palette, Menu, X, ExternalLink, ClipboardList, Tags, Images, Sparkles, Bell, BellRing } from 'lucide-react';
import { Logo } from '../../components/Header';
import { useQueue } from '../../context/QueueContext';
import { isWorkTime, minutesUntilClose, closingTimeLabel, SCHEDULE_LINES } from '../../utils/workHours';
import { pushState, enablePush, disablePush } from '../../utils/push';

const ERROR_MESSAGES = {
  credenciais: 'Usuário ou senha incorretos.',
  inativo: 'Este usuário está desativado. Fale com o administrador.',
};

export function AdminLogin() {
  const { loginAs, sessionEnded, clearSessionEnded } = useQueue();
  const navigate = useNavigate();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const res = await loginAs(login, password);
    if (!res.ok) {
      setError(res.reason);
      return;
    }
    navigate('/admin/dashboard');
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-navy-light/40 px-4 py-12" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-navy/10 bg-white p-8 shadow-sm" noValidate>
        <Logo compact />
        <p className="mt-1 text-xs text-muted-foreground">Área interna · acesso restrito a funcionários</p>

        {sessionEnded && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" role="alert" data-testid="session-ended-notice">
            Sua sessão foi encerrada com segurança ao fim do horário de atendimento. Suas alterações
            registradas foram preservadas.
            <button type="button" onClick={clearSessionEnded} className="mt-1 block text-xs font-semibold underline" data-testid="session-ended-dismiss">Entendi</button>
          </div>
        )}
        {error === 'fora_horario' && (
          <div className="mt-4 rounded-xl border border-navy/20 bg-navy-light p-4" role="alert" data-testid="login-blocked-notice">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy">
              <Clock className="h-4 w-4" /> Área de vendedores disponível durante o horário de atendimento da loja.
            </p>
            <ul className="mt-2 space-y-0.5 text-sm text-foreground/70">
              {SCHEDULE_LINES.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </div>
        )}
        {(error === 'credenciais' || error === 'inativo') && (
          <p className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm font-medium text-red-700" role="alert" data-testid={`login-error-${error}`}>
            {ERROR_MESSAGES[error]}
          </p>
        )}

        <label htmlFor="login-username" className="mt-6 block text-sm font-semibold text-navy">Usuário</label>
        <input
          id="login-username"
          data-testid="login-username-input"
          value={login}
          onChange={(e) => { setLogin(e.target.value); setError(null); }}
          autoComplete="username"
          autoCapitalize="none"
          className="mt-1.5 h-12 w-full rounded-lg border border-input px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
        />
        <label htmlFor="login-password" className="mt-4 block text-sm font-semibold text-navy">Senha</label>
        <div className="relative mt-1.5">
          <input
            id="login-password"
            data-testid="login-password-input"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            autoComplete="current-password"
            className="h-12 w-full rounded-lg border border-input px-4 pr-12 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            data-testid="login-toggle-password"
            className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-navy-light"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <button
          type="submit"
          data-testid="vendor-login-button"
          className="mt-6 w-full rounded-full bg-navy py-3.5 text-sm font-semibold tracking-wide text-white hover:bg-navy-hover"
        >
          ENTRAR
        </button>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Acesso interno. A senha é validada com hash seguro no servidor.
        </p>
      </form>
      <Link to="/" className="mt-5 text-xs font-semibold text-muted-foreground hover:text-navy" data-testid="login-back-to-site">
        ← Voltar ao site
      </Link>
    </main>
  );
}

function PanelBrand() {
  return (
    <div>
      <p className="font-serif text-lg leading-none text-navy">
        H<span className="font-normal">UDDERSFIELD</span>
      </p>
      <p className="text-[9px] font-semibold tracking-[0.3em] text-navy/80">TECIDOS</p>
      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Painel Huddersfield</p>
    </div>
  );
}

export function AdminLayout() {
  const { user, logout } = useQueue();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [minsLeft, setMinsLeft] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isVendor = user?.role === 'vendedor';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isVendor) return;
    const tick = () => setMinsLeft(minutesUntilClose());
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [isVendor]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location]);

  if (!user) {
    navigate('/admin', { replace: true });
    return null;
  }

  const closing = closingTimeLabel();
  const showWarning = isVendor && isWorkTime() && minsLeft !== null && minsLeft <= 15;

  const menuItems = [
    { to: '/admin/dashboard', hash: '', label: 'Solicitações', icon: LayoutDashboard, testid: 'admin-nav-dashboard' },
    ...(isVendor
      ? [{ to: '/admin/dashboard', hash: '#meus-atendimentos', label: 'Meus Atendimentos', icon: ClipboardList, testid: 'admin-nav-mine' }]
      : []),
    { to: '/admin/disponibilidade', hash: '', label: 'Disponibilidade', icon: RefreshCw, testid: 'admin-nav-availability' },
    { to: '/admin/revisao', hash: '', label: 'Revisão', icon: ClipboardCheck, testid: 'admin-nav-review' },
    { to: '/admin/fotos', hash: '', label: 'Fotos', icon: Images, testid: 'admin-nav-photos' },
    ...(isAdmin
      ? [
          { to: '/admin/produtos', hash: '', label: 'Produtos', icon: Package, testid: 'admin-nav-products' },
          { to: '/admin/categorias', hash: '', label: 'Categorias', icon: Tags, testid: 'admin-nav-categories' },
          { to: '/admin/usuarios', hash: '', label: 'Usuários', icon: Users, testid: 'admin-nav-users' },
          { to: '/admin/integracoes-ia', hash: '', label: 'Integrações de IA', icon: Sparkles, testid: 'admin-nav-ai' },
          { to: '/admin/personalizacao', hash: '', label: 'Personalização', icon: Palette, testid: 'admin-nav-personalization' },
        ]
      : []),
  ];

  const isActive = (item) =>
    item.hash ? location.hash === item.hash : pathname.startsWith(item.to) && !location.hash;

  const linkCls = (active) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
      active ? 'bg-navy text-white' : 'text-navy hover:bg-navy-light'
    }`;

  const userBlock = (testid) => (
    <div className="rounded-xl bg-navy-light/60 px-4 py-3" data-testid={testid}>
      <p className="truncate text-sm font-semibold text-navy">{user.name}</p>
      <p className="text-xs text-muted-foreground">{isAdmin ? 'Administrador(a)' : 'Vendedor(a)'}</p>
    </div>
  );

  const verSite = (testid) => (
    <a
      href="/"
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-navy hover:bg-navy-light"
    >
      <ExternalLink className="h-4 w-4" /> Ver site
    </a>
  );

  const sair = (testid) => (
    <button
      type="button"
      onClick={() => {
        logout();
        navigate('/admin');
      }}
      data-testid={testid}
      className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
    >
      <LogOut className="h-4 w-4" /> Sair
    </button>
  );

  const navLinks = (suffix = '') =>
    menuItems.map((item) => (
      <Link
        key={item.testid + suffix}
        to={item.to + item.hash}
        className={linkCls(isActive(item))}
        data-testid={item.testid + suffix}
        aria-current={isActive(item) ? 'page' : undefined}
      >
        <item.icon className="h-4 w-4 shrink-0" /> {item.label}
      </Link>
    ));

  return (
    <div className="flex min-h-screen bg-navy-light/30" data-testid="admin-shell">
      <aside
        className="hidden md:sticky md:top-0 md:flex md:h-screen md:w-64 md:shrink-0 md:flex-col md:border-r md:border-navy/10 md:bg-white"
        data-testid="admin-sidebar"
      >
        <div className="border-b border-navy/10 px-5 py-5">
          <PanelBrand />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" aria-label="Menu do painel">
          {navLinks()}
        </nav>
        <div className="space-y-1 border-t border-navy/10 p-3">
          {userBlock('admin-current-user')}
          <PushToggle testid="push-toggle" />
          {verSite('admin-ver-site')}
          {sair('admin-logout-button')}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-50 flex items-center gap-3 border-b border-navy/10 bg-white px-4 py-3 md:hidden" data-testid="admin-topbar-mobile">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu do painel"
            data-testid="admin-menu-button"
            className="rounded-full p-2 text-navy hover:bg-navy-light"
          >
            <Menu className="h-6 w-6" />
          </button>
          <p className="font-serif text-base leading-none text-navy">
            H<span className="font-normal">UDDERSFIELD</span>
          </p>
          <span className="ml-auto text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Painel</span>
        </header>

        <main className="flex-1 px-4 py-6 md:py-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[80] md:hidden" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-[85%] max-w-xs flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-navy/10 px-5 py-4">
              <PanelBrand />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Fechar menu"
                data-testid="admin-drawer-close"
                className="rounded-full p-2 text-navy hover:bg-navy-light"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4">{userBlock('admin-current-user-mobile')}</div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4" aria-label="Menu do painel mobile">
              {navLinks('-mobile')}
            </nav>
            <div className="space-y-1 border-t border-navy/10 p-3">
              <PushToggle testid="push-toggle-mobile" />
              {verSite('admin-ver-site-mobile')}
              {sair('admin-logout-button-mobile')}
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <div
          className="fixed inset-x-0 bottom-0 z-[70] flex items-center justify-center gap-2 bg-amber-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-lg"
          role="alert"
          data-testid="closing-warning-banner"
        >
          <Clock className="h-4 w-4 shrink-0" />
          Seu horário de acesso termina às {closing}. Salve as alterações em andamento.
        </div>
      )}
    </div>
  );
}
// Ativação de notificações push por aparelho (Web Push): sempre via clique, exigência dos browsers.
function PushToggle({ testid }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    pushState().then(setState).catch(() => setState('unsupported'));
  }, []);
  if (!state || state === 'unsupported') return null;
  const enabled = state === 'enabled';
  const toggle = async () => {
    setBusy(true);
    setError('');
    try {
      if (enabled) await disablePush(); else await enablePush();
      setState(enabled ? 'disabled' : 'enabled');
    } catch (e) {
      setError(e.message || 'Não foi possível alterar as notificações.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={enabled}
        data-testid={testid}
        className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${enabled ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'text-navy hover:bg-navy-light'}`}
      >
        {enabled ? <BellRing className="h-4 w-4 shrink-0" /> : <Bell className="h-4 w-4 shrink-0" />}
        {busy ? 'Aguarde…' : enabled ? 'Avisos ativados neste aparelho' : 'Receber avisos neste aparelho'}
      </button>
      {state === 'denied' && (
        <p className="px-4 pb-1 text-[11px] leading-snug text-amber-700" data-testid={`${testid}-denied`}>
          Notificações bloqueadas no navegador — libere nas configurações do aparelho e tente de novo.
        </p>
      )}
      {error && (
        <p className="px-4 pb-1 text-[11px] text-red-600" role="alert" data-testid={`${testid}-error`}>{error}</p>
      )}
    </div>
  );
}


// Áreas exclusivas de administrador: vendedor que abrir o endereço direto vê este aviso.
export function AdminOnly({ children }) {
  const { user } = useQueue();
  if (user?.role !== 'admin') {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="admin-only-block">
        <Lock className="mx-auto h-10 w-10 text-navy/40" strokeWidth={1.5} />
        <p className="mt-4 font-serif text-2xl text-navy">Acesso restrito a administradores</p>
        <p className="mt-2 text-sm text-muted-foreground">Esta área não está disponível para o perfil de vendedor(a).</p>
        <Link to="/admin/dashboard" className="mt-6 inline-block text-sm font-semibold text-navy underline" data-testid="admin-only-back">
          Voltar ao painel
        </Link>
      </div>
    );
  }
  return children;
}